import { useGameParamsContext } from "./gameParams";
import React from "react";
import { getAvatarId } from "../utils/jwt";
import { setUser as setUserInSentry } from "../tracing/sentry";
import { useSnowplowContext } from "../analytics/snowplow";
import { bridge } from "../utils/bridge";

const JWT_MAX_RETRIES = 3;

type SessionState = {
    jwt: string | null;
    avatarId: number | null;
};

const defaultState: SessionState = {
    jwt: null,
    avatarId: null,
};

const SessionContext = React.createContext<{
    state: SessionState;
    actions: {
        refreshJwt: () => Promise<void>;
    };
}>({
    state: defaultState,
    actions: {
        refreshJwt: () => Promise.resolve(),
    },
});

const SessionContextProvider = ({
    children,
}: {
    children: JSX.Element | JSX.Element[] | React.ReactNode;
}): JSX.Element | null => {
    const { gameParams } = useGameParamsContext();

    const { setUser: setUserInSnowplow } = useSnowplowContext();
    const [state, setState] = React.useState<SessionState>(defaultState);
    const intervalId = React.useRef<number | null>(null);
    const retries = React.useRef<number>(0);

    const clearInterval = React.useCallback(() => {
        if (intervalId.current) {
            window.clearInterval(intervalId.current);
            intervalId.current = null;
        }
    }, [intervalId.current]);

    const setJwt = React.useCallback(
        (jwt: string | null) => {
            const avatarId = jwt ? (getAvatarId(jwt) ?? null) : null;
            setState({ jwt, avatarId });
            if (avatarId) {
                if (window.SENTRY_DSN) setUserInSentry(avatarId);
                setUserInSnowplow(avatarId);
            }
        },
        [setUserInSnowplow],
    );

    React.useEffect(() => {
        const { jwt, offline } = gameParams;
        /* FIXME:
          Remove this when native side changes are merged in all games.
          We only need to do this because the old webview plasters over the
          asynchronous nature of getting the JWT from the game server. So
          sometimes the jwt isn't in the initial `getParams` call
        */

        if (!jwt && offline === undefined) {
            console.log("NO_JWT");
            if (!intervalId.current) {
                const id = window.setInterval(() => {
                    if (
                        retries.current >= JWT_MAX_RETRIES &&
                        intervalId.current
                    ) {
                        console.log("MAX_RETRIES");
                        clearInterval();

                        // Dev fallback: use JWT from localStorage when no native bridge
                        if (process.env.NODE_ENV !== "production") {
                            const devJwt = localStorage.getItem("dev-jwt");
                            if (devJwt) {
                                window.dispatchEvent(
                                    new CustomEvent("jwtUpdated", {
                                        detail: { jwt: devJwt },
                                    }),
                                );
                                return;
                            }
                        }

                        window.sendCommand?.("tokenExpired");
                        return;
                    }

                    retries.current++;
                    console.log("GET_PARAMS");
                    window.sendCommand?.("getParams");
                }, 250);
                intervalId.current = id;
            }
        } else {
            console.log("CLEAR_INTERVAL");
            clearInterval();
        }

        return () => {
            clearInterval();
        };
    }, [gameParams]);

    React.useEffect(() => {
        const { jwt } = gameParams;
        if (jwt) setJwt(jwt);
    }, [gameParams, setJwt]);

    React.useEffect(() => {
        const onJwtUpdated = (e: Event) => {
            const { jwt } = (e as CustomEvent<{ jwt?: string }>).detail ?? {};
            if (jwt) setJwt(jwt);
        };
        window.addEventListener("jwtUpdated", onJwtUpdated);
        return () => window.removeEventListener("jwtUpdated", onJwtUpdated);
    }, [setJwt]);

    const refreshJwt = React.useCallback(async () => {
        const b = await bridge();
        await b.refreshJwt();
    }, []);

    return (
        <SessionContext.Provider value={{ state, actions: { refreshJwt } }}>
            {children}
        </SessionContext.Provider>
    );
};

export const withSessionContextProvider =
    (Component: typeof React.Component) => (props: Record<string, unknown>) => {
        return (
            <SessionContextProvider>
                <Component {...props} />
            </SessionContextProvider>
        );
    };

export const SessionContextProviderComponent = ({
    children,
}: {
    children: React.ReactNode;
}) => {
    return <SessionContextProvider>{children}</SessionContextProvider>;
};

export const useSessionContext = () => React.useContext(SessionContext);
